import { useEffect, useState } from 'react';
import { i18n, staffFeatureKeys, type UpdateStaffAccessInput } from '@wms/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import {
  PartnerFeaturesEditor,
  type PartnerFeatureUser,
} from '../components/PartnerFeaturesEditor.js';

const { t } = i18n;
type Scope = UpdateStaffAccessInput['scopes'][number];
interface Staff {
  id: string;
  email: string;
  displayName: string;
  status: string;
  ownerAdmin: boolean;
  staffRestricted: boolean;
  staffSkuCreate?: boolean | null;
  staffSkuEdit?: boolean | null;
  staffSkuPhotoUpload?: boolean | null;
  roles: { role: string }[];
  staffScopes: Scope[];
}
interface AccessData {
  users: Staff[];
  partnerUsers?: PartnerFeatureUser[];
  partners: { id: string; name: string }[];
  customers: { id: string; name: string }[];
}

export function StaffAccessPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState<AccessData | null>(null);
  const [selected, setSelected] = useState('');
  const [draft, setDraft] = useState<UpdateStaffAccessInput | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<AccessData>('/staff-access')
      .then(setData)
      .catch(() => setError(t('staff.load_error')));
  }, []);
  const choose = (id: string) => {
    setSelected(id);
    setError('');
    setSaved(false);
    const account = data?.users.find((u) => u.id === id);
    setDraft(
      account
        ? {
            role: account.roles.some((r) => r.role === 'ADMIN') ? 'ADMIN' : 'OPERATOR',
            ownerAdmin: account.ownerAdmin,
            restricted:
              account.staffRestricted ||
              (!account.ownerAdmin && account.roles.some((r) => r.role === 'ADMIN')),
            scopes: account.staffScopes.map((s) => ({
              scopeType: s.scopeType,
              scopeId: s.scopeId,
              canWrite: s.canWrite,
            })),
            features: {
              skuCreate: account.staffSkuCreate ?? null,
              skuEdit: account.staffSkuEdit ?? null,
              skuPhotoUpload: account.staffSkuPhotoUpload ?? null,
            },
          }
        : null,
    );
  };
  const scope = (
    scopeType: Scope['scopeType'],
    scopeId: string,
    mode: 'read' | 'write',
    checked: boolean,
  ) => {
    if (!draft) return;
    const others = draft.scopes.filter((s) => s.scopeType !== scopeType || s.scopeId !== scopeId);
    const current = draft.scopes.find((s) => s.scopeType === scopeType && s.scopeId === scopeId);
    const next =
      mode === 'read' && !checked
        ? others
        : [
            ...others,
            {
              scopeType,
              scopeId,
              canWrite: mode === 'write' ? checked : (current?.canWrite ?? false),
            },
          ];
    setDraft({ ...draft, scopes: next });
    setSaved(false);
  };
  const save = async () => {
    if (!draft || !data) return;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const updated = await api<Staff>(`/staff-access/${selected}`, { method: 'PUT', body: draft });
      setData({ ...data, users: data.users.map((u) => (u.id === selected ? updated : u)) });
      setSaved(true);
      if (selected === user?.id) await logout();
    } catch {
      setError(t('staff.save_error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">{t('staff.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('staff.description')}</p>
      {error && !draft && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      <label className="block">
        {t('staff.account')}
        <select
          className="ml-2 rounded border p-2"
          value={selected}
          disabled={busy}
          onChange={(e) => choose(e.target.value)}
        >
          <option value="">{t('staff.choose')}</option>
          {data?.users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName} ({u.email}){u.status !== 'ACTIVE' ? ` — ${t('staff.disabled')}` : ''}
            </option>
          ))}
        </select>
      </label>
      {draft && data && (
        <fieldset disabled={busy} className="space-y-4">
          <label className="block">
            {t('staff.role')}{' '}
            <select
              className="rounded border p-2"
              value={draft.role}
              onChange={(e) => {
                const role = e.target.value as UpdateStaffAccessInput['role'];
                setDraft({
                  ...draft,
                  role,
                  ownerAdmin: role === 'ADMIN' && draft.ownerAdmin,
                  restricted: role === 'ADMIN' ? !draft.ownerAdmin : draft.restricted,
                });
                setSaved(false);
              }}
            >
              <option value="ADMIN">{t('role.ADMIN')}</option>
              <option value="OPERATOR">{t('role.OPERATOR')}</option>
            </select>
          </label>
          {draft.role === 'ADMIN' && (
            <label className="block">
              <input
                type="checkbox"
                checked={draft.ownerAdmin}
                onChange={(e) => {
                  setDraft({
                    ...draft,
                    ownerAdmin: e.target.checked,
                    restricted: !e.target.checked,
                  });
                  setSaved(false);
                }}
              />{' '}
              {t('staff.owner')}
            </label>
          )}
          {draft.role === 'OPERATOR' && (
            <label className="block">
              <input
                type="checkbox"
                checked={draft.restricted}
                onChange={(e) => {
                  setDraft({ ...draft, restricted: e.target.checked });
                  setSaved(false);
                }}
              />{' '}
              {t('staff.restricted')}
            </label>
          )}
          {!draft.restricted && <p>{t('staff.unrestricted')}</p>}
          {!draft.ownerAdmin && (
            <section className="space-y-2" aria-label={t('staff.features.title')}>
              <h3 className="font-medium">{t('staff.features.title')}</h3>
              <p className="text-sm text-muted-foreground">{t('staff.features.hint')}</p>
              {staffFeatureKeys.map((key) => (
                <label key={key} className="block">
                  {t(`staff.features.${key}`)}{' '}
                  <select
                    aria-label={t(`staff.features.${key}`)}
                    className="rounded border p-2"
                    value={draft.features?.[key] == null ? 'default' : String(draft.features[key])}
                    onChange={(e) => {
                      setDraft({
                        ...draft,
                        features: {
                          skuCreate: null,
                          skuEdit: null,
                          skuPhotoUpload: null,
                          ...draft.features,
                          [key]: e.target.value === 'default' ? null : e.target.value === 'true',
                        },
                      });
                      setSaved(false);
                    }}
                  >
                    <option value="default">{t('staff.features.default')}</option>
                    <option value="true">{t('staff.features.allow')}</option>
                    <option value="false">{t('staff.features.deny')}</option>
                  </select>
                </label>
              ))}
            </section>
          )}
          {draft.restricted && (
            <>
              <p className="text-sm">{t('staff.scope_hint')}</p>
              {(['PARTNER', 'STORAGE_CUSTOMER'] as const).map((type) => (
                <div key={type}>
                  <h3 className="font-medium">
                    {t(type === 'PARTNER' ? 'nav.partners' : 'nav.customers')}
                  </h3>
                  <table className="w-full text-left">
                    <thead>
                      <tr>
                        <th>{t('staff.entity')}</th>
                        <th>{t('staff.read')}</th>
                        <th>{t('staff.write')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(type === 'PARTNER' ? data.partners : data.customers).map((entity) => {
                        const grant = draft.scopes.find(
                          (s) => s.scopeType === type && s.scopeId === entity.id,
                        );
                        return (
                          <tr key={entity.id}>
                            <td>{entity.name}</td>
                            <td>
                              <input
                                aria-label={`${entity.name}: ${t('staff.read')}`}
                                type="checkbox"
                                checked={Boolean(grant)}
                                onChange={(e) => scope(type, entity.id, 'read', e.target.checked)}
                              />
                            </td>
                            <td>
                              <input
                                aria-label={`${entity.name}: ${t('staff.write')}`}
                                type="checkbox"
                                checked={Boolean(grant?.canWrite)}
                                onChange={(e) => scope(type, entity.id, 'write', e.target.checked)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
          <p className="text-sm text-muted-foreground">{t('staff.sessions')}</p>
          <div className="space-y-2">
            <button className="rounded border px-4 py-2" onClick={() => void save()}>
              {t(busy ? 'staff.saving' : 'staff.save')}
            </button>
            {saved && (
              <p role="status" aria-live="polite" className="rounded border p-3 font-medium">
                {t('staff.saved')}
              </p>
            )}
            {error && (
              <p role="alert" className="rounded border border-destructive p-3 text-destructive">
                {error}
              </p>
            )}
          </div>
        </fieldset>
      )}
      {data && <PartnerFeaturesEditor users={data.partnerUsers ?? []} />}
    </section>
  );
}
